import { useState, useMemo } from 'react'
import { Calendar, Infinity, GraduationCap, BookOpen, Settings } from 'lucide-react'

type PresetType = 'unlimited' | 'school_year' | 'semester' | 'custom'

interface ValidityPeriodPickerProps {
  validFrom: string
  validTo: string
  onChange: (validFrom: string, validTo: string) => void
  schoolYearStartMonth?: number
}

function getSchoolYearRange(startMonth: number): { from: string; to: string } {
  const now = new Date()
  const year = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  let startYear = year
  if (currentMonth < startMonth) {
    startYear = year - 1
  }
  const endYear = startYear + 1

  const from = `${startYear}-${String(startMonth).padStart(2, '0')}-01`
  
  // End date is the last day of the month before startMonth
  const endMonth = startMonth === 1 ? 12 : startMonth - 1
  const lastDay = new Date(endYear, startMonth, 0).getDate()
  const to = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
  
  return { from, to }
}

function getSemesterRange(startMonth: number): { from: string; to: string } {
  const now = new Date()
  const year = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  // Determine which school year we're in
  // If current month >= startMonth, we're in the school year that starts this year
  // Otherwise, we're in the school year that started last year
  const schoolYear = currentMonth >= startMonth ? year : year - 1
  
  // Determine which semester we're in
  // First semester: startMonth to startMonth+5 (wrapping around year boundary)
  // Second semester: startMonth+6 to startMonth+11 (wrapping around year boundary)
  
  let semesterStartMonth: number
  let semesterEndMonth: number
  let semesterYear: number

  // Calculate months since school year start (0-11)
  const monthsSinceStart = currentMonth >= startMonth 
    ? currentMonth - startMonth 
    : currentMonth + 12 - startMonth
  
  if (monthsSinceStart < 6) {
    // First semester
    semesterStartMonth = startMonth
    semesterEndMonth = startMonth + 5
    if (semesterEndMonth > 12) semesterEndMonth -= 12
    semesterYear = schoolYear
  } else {
    // Second semester
    semesterStartMonth = startMonth + 6
    if (semesterStartMonth > 12) semesterStartMonth -= 12
    semesterEndMonth = startMonth + 11
    if (semesterEndMonth > 12) semesterEndMonth -= 12
    semesterYear = schoolYear + (semesterStartMonth < startMonth ? 1 : 0)
  }

  const from = `${semesterYear}-${String(semesterStartMonth).padStart(2, '0')}-01`
  
  // End date is the last day of semesterEndMonth
  const endYear = semesterEndMonth < semesterStartMonth ? semesterYear + 1 : semesterYear
  const lastDay = new Date(endYear, semesterEndMonth, 0).getDate()
  const to = `${endYear}-${String(semesterEndMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
  
  return { from, to }
}

export function ValidityPeriodPicker({
  validFrom,
  validTo,
  onChange,
  schoolYearStartMonth = 8,
}: ValidityPeriodPickerProps) {
  const [activePreset, setActivePreset] = useState<PresetType>(() => {
    if (!validFrom && !validTo) return 'unlimited'
    return 'custom'
  })

  const presets = useMemo(() => {
    const sy = getSchoolYearRange(schoolYearStartMonth)
    const sem = getSemesterRange(schoolYearStartMonth)
    return {
      unlimited: { from: '', to: '' },
      school_year: sy,
      semester: sem,
    }
  }, [schoolYearStartMonth])

  const handlePresetClick = (preset: PresetType) => {
    setActivePreset(preset)
    if (preset === 'unlimited') {
      onChange('', '')
    } else if (preset === 'school_year') {
      onChange(presets.school_year.from, presets.school_year.to)
    } else if (preset === 'semester') {
      onChange(presets.semester.from, presets.semester.to)
    }
  }

  const handleCustomFromChange = (value: string) => {
    setActivePreset('custom')
    onChange(value, validTo)
  }

  const handleCustomToChange = (value: string) => {
    setActivePreset('custom')
    onChange(validFrom, value)
  }

  const presetButtons: { type: PresetType; label: string; icon: React.ReactNode; description: string }[] = [
    {
      type: 'unlimited',
      label: 'Unbegrenzt',
      icon: <Infinity className="h-4 w-4" />,
      description: 'Keine Einschränkung',
    },
    {
      type: 'school_year',
      label: 'Schuljahr',
      icon: <GraduationCap className="h-4 w-4" />,
      description: `${presets.school_year.from} – ${presets.school_year.to}`,
    },
    {
      type: 'semester',
      label: 'Semester',
      icon: <BookOpen className="h-4 w-4" />,
      description: `${presets.semester.from} – ${presets.semester.to}`,
    },
    {
      type: 'custom',
      label: 'Benutzerdefiniert',
      icon: <Settings className="h-4 w-4" />,
      description: 'Eigener Zeitraum',
    },
  ]

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        {presetButtons.map((preset) => (
          <button
            key={preset.type}
            type="button"
            onClick={() => handlePresetClick(preset.type)}
            className={`flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-colors ${
              activePreset === preset.type
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className={`shrink-0 ${activePreset === preset.type ? 'text-blue-600' : 'text-gray-500'}`}>
              {preset.icon}
            </div>
            <div className="min-w-0">
              <div className={`text-sm font-medium ${activePreset === preset.type ? 'text-blue-700' : 'text-gray-700'}`}>
                {preset.label}
              </div>
              <div className="text-xs text-gray-500 truncate">{preset.description}</div>
            </div>
          </button>
        ))}
      </div>

      {activePreset === 'custom' && (
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div>
            <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
              <Calendar className="h-3 w-3" />
              Gültig ab
            </label>
            <input
              type="date"
              value={validFrom}
              onChange={(e) => handleCustomFromChange(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
              <Calendar className="h-3 w-3" />
              Gültig bis
            </label>
            <input
              type="date"
              value={validTo}
              onChange={(e) => handleCustomToChange(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}

      {activePreset !== 'custom' && activePreset !== 'unlimited' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
          <strong>{presetButtons.find(p => p.type === activePreset)?.label}:</strong>{' '}
          {activePreset === 'school_year' && `${presets.school_year.from} – ${presets.school_year.to}`}
          {activePreset === 'semester' && `${presets.semester.from} – ${presets.semester.to}`}
        </div>
      )}
    </div>
  )
}
